import React, { useState, useEffect } from "react";
import * as api from "../api/api";
import Loading from "../Components/Loading";
import { Modal, Button, Form } from "react-bootstrap";
import { Snackbar, Alert, Select, MenuItem } from "@mui/material";
import checkAuthorization from "../config/checkAuth";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../Components/Localization/LanguageContext";
import EditIcon from "@mui/icons-material/Edit";
import InputGroup from "react-bootstrap/InputGroup";
import { BiSearch } from "react-icons/bi";
import { useDebounce } from "@uidotdev/usehooks";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import LastPageIcon from "@mui/icons-material/LastPage";
import { Button as MaterialButton } from "@mui/material";

// Manually define the strings object for AdminHouseManagement
const strings = {
  AdminHouseManagement: {
    en: {
      pageTitle: "House Management",
      addNew: "Add New House",
      name: "House Name",
      close: "Close",
      saveChanges: "Save Changes",
      add: "Add House",
      created: "House created successfully!",
      editedSuccess: "House updated successfully!",
      deactivated: "House deactivated!",
      activated: "House activated!",
      deactivationModalTitle: "Deactivate House?",
      deactivationModalBody:
        "Are you sure you want to deactivate this house? This action cannot be undone.",
      tableHeaders: ["House ID", "House Name", "House DID", "Edit"],
      search: "Search by house name",
      page: "Page",
      of: "of",
      items: "items",
      first: "First",
      previous: "Previous",
      next: "Next",
      last: "Last",
    },
    de: {
      // German translations
      pageTitle: "Hausverwaltung",
      addNew: "Neues Haus hinzufügen",
      name: "Hausname",
      close: "Schließen",
      saveChanges: "Änderungen speichern",
      add: "Haus hinzufügen",
      created: "Haus erfolgreich erstellt!",
      editedSuccess: "Haus erfolgreich aktualisiert!",
      deactivated: "Haus deaktiviert!",
      activated: "Haus aktiviert!",
      deactivationModalTitle: "Haus deaktivieren?",
      deactivationModalBody:
        "Sind Sie sicher, dass Sie dieses Haus deaktivieren möchten? Diese Aktion kann nicht rückgängig gemacht werden.",
      tableHeaders: ["Haus-ID", "Hausname", "DID", "Bearbeiten"],
      search: "Nach Hausnamen suchen",
      page: "Seite",
      of: "von",
      items: "Elemente",
      first: "Erste",
      previous: "Zurück",
      next: "Weiter",
      last: "Letzte",
    },
  },
};

function AdminHouseManagement() {
  const { language } = useLanguage();
  const pageTitle = "AdminHouseManagement";
  const [isLoading, setLoading] = useState(true);
  const [houseList, setHouseList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newHouse, setNewHouse] = useState(""); // Initialize with empty string
  const [snackbarData, setSnackbarData] = useState({});
  const navigate = useNavigate();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [numberOfPages, setNumberOfPages] = useState(1);
  const [numberOfItems, setNumberOfItems] = useState(0);

  // Search states
  const [inputValue, setInputValue] = useState("");
  const [searchRunning, setSearchRunning] = useState(false);
  const searchValue = useDebounce(inputValue, 1000); // 500ms delay for debounce
  const [searchParam, setSearchParam] = useState("name");

  const getHouses = (pageNo = 1, houseName = "") => {
    setSearchRunning(true);
    setLoading(true);

    const currentPageNo = pageNo < 1 ? 1 : pageNo;
    let searchValueEdited = searchValue.trim();

    if (searchParam == "dId") {
      searchValueEdited = searchValueEdited.replace("+", "%2B");
    }

    api
      .getAllHouses(30, currentPageNo, searchParam, searchValueEdited)
      .then((response) => {
        let housesFromApi = response.data.object;

        // Apply filtering if a search name is provided
        let filteredHouses = housesFromApi.houses;

        // Update pagination and house list states
        setHouseList(filteredHouses);
        setNumberOfPages(
          housesFromApi.totalPages === 0 ? 1 : housesFromApi.totalPages
        );
        setNumberOfItems(housesFromApi.totalElements);

        setSearchRunning(false);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching houses:", error);
        setSearchRunning(false);
        setLoading(false);
        setSnackbarData({
          message: "Failed to fetch houses.", // More user-friendly error message
          show: true,
          severity: "error",
        });
      });
  };

  useEffect(() => {
    checkAuthorization("HOUSE_MANAGEMENT").then((result) => {
      if (result) {
        getHouses(currentPage, "");
      } else {
        navigate("/unauthorized");
      }
    });
  }, []);

  useEffect(() => {
    setSearchRunning(true);
    if (searchValue.length < 3 && searchValue.length !== 0) {
      setSearchRunning(false);
      return;
    }
    setCurrentPage(1);
    getHouses(1, searchValue);
  }, [searchValue]);

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const createHouse = (event) => {
    event.preventDefault();
    setShowModal(false);
    setLoading(true);
    console.log("new house: ", newHouse);

    api
      .addNewHouse(newHouse.name, newHouse.houseId)
      .then((response) => {
        // TODO: Check if house is created successfully or not.
        // if failed, show the error from BE -- if sucess, navigate

        if (response.data.statusCode != 200) {
          setSnackbarData({
            message: response.data.message,
            show: true,
            severity: "error",
          });
        } else {
          console.log("New house added:", response.data);
          setSnackbarData({
            message: strings[pageTitle][language].created,
            show: true,
            severity: "success",
          });
          setTimeout(() => {
            navigate(`/house/${response.data.object.houseId}`); // Navigate to the new house's page
          }, 3000);
        }
      })
      .catch((error) => {
        console.error("Error creating house:", error);
        setSnackbarData({
          message: "Failed to create house.",
          show: true,
          severity: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return isLoading ? (
    <Loading />
  ) : (
    <div className="main-content-container">
      <div className="main-content">
        <div>
          <Snackbar
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            open={snackbarData.show}
            autoHideDuration={3000}
            onClose={() => setSnackbarData({ ...snackbarData, show: false })}
          >
            <Alert severity={snackbarData.severity}>
              {snackbarData.message}
            </Alert>
          </Snackbar>

          <Modal show={showModal} onHide={() => setShowModal(false)}>
            <Form onSubmit={createHouse}>
              <Modal.Header closeButton>
                <Modal.Title>{strings[pageTitle][language].addNew}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form.Group
                  className="mb-3"
                  controlId="exampleForm.ControlInput1"
                >
                  <Form.Label>{strings[pageTitle][language].name}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={
                      language === "en"
                        ? "Enter house name"
                        : "Hausnamen eingeben"
                    }
                    autoFocus
                    onChange={(e) =>
                      setNewHouse({ ...newHouse, name: e.target.value })
                    }
                    value={newHouse.name} // Controlled component for input
                  />
                  <Form.Label>{"House ID"}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={
                      language === "en" ? "Enter house ID" : "Haus-ID eingeben"
                    }
                    //autoFocus
                    onChange={(e) =>
                      setNewHouse({ ...newHouse, houseId: e.target.value })
                    }
                    value={newHouse.houseId} // Controlled component for input
                  />
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="light" onClick={() => setShowModal(false)}>
                  {strings[pageTitle][language].close}
                </Button>
                <Button variant="primary" type="submit">
                  {strings[pageTitle][language].saveChanges}
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>
          <div className="main-content__title d-flex align-items-center justify-content-between">
            <h6 className="main-content__title">
              {strings[pageTitle][language].pageTitle}
            </h6>
            <button
              className="btn btn-outline-primary"
              onClick={() => {
                setShowModal(true);
                setNewHouse(""); // Clear input when opening modal
              }}
            >
              {strings[pageTitle][language].add}
            </button>
          </div>
          <hr className="main-content-title__hr" />
          <div
            className="form-group row"
            style={{ width: "100%", marginLeft: "0px" }}
          >
            <InputGroup className="">
              <Form.Control
                placeholder={strings[pageTitle][language].search}
                aria-label="Search"
                aria-describedby="basic-addon2"
                onChange={handleInputChange}
                value={inputValue}
              />
              <Select
                onChange={(e) => {
                  setSearchParam(e.target.value);
                }}
                defaultValue={searchParam}
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="dId">DID</MenuItem>
              </Select>
              <InputGroup.Text id="basic-addon2">
                <BiSearch />
              </InputGroup.Text>
            </InputGroup>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "end",
              flexDirection: "row",
            }}
          ></div>
          <div className="main-content__body">
            {searchRunning ? (
              <Loading></Loading>
            ) : (
              <table
                style={{
                  width: "98%",
                  margin: "20px auto",
                  borderCollapse: "collapse",
                  border: "none",
                  tableLayout: "fixed",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ padding: "12px 8px", width: "10%" }}>
                      {strings[pageTitle][language].tableHeaders[0]}
                    </th>
                    <th style={{ padding: "12px 8px", width: "50%" }}>
                      {strings[pageTitle][language].tableHeaders[1]}
                    </th>
                    <th style={{ padding: "12px 8px", width: "20%" }}>
                      {strings[pageTitle][language].tableHeaders[2]}
                    </th>
                    <th style={{ padding: "12px 8px", width: "20%" }}>
                      {strings[pageTitle][language].tableHeaders[3]}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {houseList.length > 0 ? (
                    houseList.map((house) => (
                      <tr key={house.houseId}>
                        <td
                          style={{
                            padding: "12px 8px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {house.houseId}
                        </td>
                        <td
                          style={{
                            padding: "12px 8px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {house.name}
                        </td>
                        <td
                          style={{
                            padding: "12px 8px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {house.did}
                        </td>
                        <td style={{ padding: "12px 8px" }}>
                          <button
                            style={{
                              border: "none",
                              outline: "none",
                              backgroundColor: "rgba(255, 0, 0, 0)",
                              color: "#00a0e5",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                "#e0f2f7")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                "rgba(255, 0, 0, 0)")
                            }
                            onClick={() => {
                              navigate(`/house/${house.houseId}`);
                            }}
                          >
                            <EditIcon />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="4"
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        No houses found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
          <nav className="pagination-container">
            <ul className="pagination">
              <li>
                <MaterialButton
                  onClick={() => {
                    getHouses(1, inputValue);
                    setCurrentPage(1);
                  }}
                  disabled={currentPage === 1 || numberOfPages === 0}
                >
                  <FirstPageIcon></FirstPageIcon>{" "}
                  {strings[pageTitle][language].first}
                </MaterialButton>
              </li>
              <li>
                <MaterialButton
                  onClick={() => {
                    getHouses(currentPage - 1, inputValue);
                    setCurrentPage(currentPage - 1);
                  }}
                  disabled={currentPage === 1 || numberOfPages === 0}
                >
                  <ArrowBackIosIcon></ArrowBackIosIcon>{" "}
                  {strings[pageTitle][language].previous}
                </MaterialButton>
              </li>
            </ul>

            <p>
              {strings[pageTitle][language].page} {currentPage}{" "}
              {strings[pageTitle][language].of} {numberOfPages} ({numberOfItems}{" "}
              {strings[pageTitle][language].items})
            </p>
            <ul className="pagination">
              <li>
                <MaterialButton
                  onClick={() => {
                    getHouses(currentPage + 1, inputValue);
                    setCurrentPage(currentPage + 1);
                  }}
                  disabled={
                    currentPage === numberOfPages || numberOfPages === 0
                  }
                >
                  {strings[pageTitle][language].next}{" "}
                  <ArrowForwardIosIcon></ArrowForwardIosIcon>
                </MaterialButton>
              </li>
              <li>
                <MaterialButton
                  onClick={() => {
                    getHouses(numberOfPages, inputValue);
                    setCurrentPage(numberOfPages);
                  }}
                  disabled={
                    currentPage === numberOfPages || numberOfPages === 0
                  }
                >
                  {strings[pageTitle][language].last}
                  <LastPageIcon></LastPageIcon>
                </MaterialButton>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  );
}

export default AdminHouseManagement;
